import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { useSelector } from "react-redux";
import { GlobalService } from "../../modules/shared/services/global.service";
import { useAppDispatch, getFavoriteByUserId, updateFavorite, createFavorite } from "../../redux";
import { useTranslation } from "react-i18next";

interface FavoriteButtonProps {
    targetUserId: string;
    className?: string;
    size?: "sm" | "md" | "lg";
    showText?: boolean;
    favoriteData?: {
        id?: string;
        isFavorite?: boolean;
    };
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
    targetUserId,
    className = "",
    size = "md",
    showText = false,
    favoriteData
}) => {
    const { t } = useTranslation();
    // Don't render if targetUserId is empty or invalid
    if (!targetUserId || targetUserId === "" || targetUserId === "{}" || targetUserId === "undefined") {

        return null;
    }
    const { isAuthenticated, user } = useSelector((state: any) => state.auth);
    const { currentUserFavorite } = useSelector((state: any) => state.favorites);
    const dispatch = useAppDispatch();

    // Favorite functionality state
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);

    // Use favoriteData for initial state, but still allow API calls for toggling
    useEffect(() => {
        if (favoriteData && favoriteData.isFavorite !== undefined) {
            // Use favorite data from professional object for initial state
            setIsFavorite(favoriteData.isFavorite);
        } else {
            // Fallback to original API-based logic if favoriteData is not provided
            if (targetUserId && targetUserId !== "" && targetUserId !== "undefined") {
                loadFavoriteStatus();
            }
        }
    }, [targetUserId, isAuthenticated, user?.id, favoriteData]);

    // Update favorite status when currentUserFavorite changes
    useEffect(() => {
        // Only update from API if we don't have favoriteData or if we're not using it
        if (favoriteData && favoriteData.isFavorite !== undefined) {
            // If we have favoriteData, only update from API if the user manually toggled
            // This will be handled by the toggle function
            return;
        }

        if (isAuthenticated && currentUserFavorite && currentUserFavorite.userIds && targetUserId) {
            const isFavorited = currentUserFavorite.userIds.some(user => user.id === targetUserId);
            setIsFavorite(isFavorited);
        } else if (isAuthenticated && !currentUserFavorite) {
            // If no favorites record exists, the profile is not favorited
            setIsFavorite(false);
        } else if (!isAuthenticated && targetUserId) {
            // For unauthenticated users, check localStorage
            const checkLocalStorage = async () => {
                try {
                    const favorites = await GlobalService.getStorage("favoriteUserIds");
                    const favoriteIds = Array.isArray(favorites) ? favorites : [];
                    const isFavorited = favoriteIds.includes(targetUserId);
                    console.log("FavoriteButton: LocalStorage check - favorites:", favoriteIds, "targetUserId:", targetUserId, "isFavorited:", isFavorited);
                    setIsFavorite(isFavorited);
                } catch (error) {
                    // Handle error silently
                }
            };
            checkLocalStorage();
        }
    }, [currentUserFavorite, isAuthenticated, targetUserId, favoriteData]);

    // Sync localStorage favorites with backend when user becomes authenticated
    useEffect(() => {
        const syncLocalFavoritesWithBackend = async () => {
            if (isAuthenticated && user?.id && targetUserId) {
                try {
                    const localFavorites = await GlobalService.getStorage("favoriteUserIds");
                    const localFavoriteIds = Array.isArray(localFavorites) ? localFavorites : [];
                    
                    if (localFavoriteIds.includes(targetUserId)) {
                        // If this user is in localStorage favorites, ensure it's also in backend
                        if (!currentUserFavorite) {
                            await dispatch(createFavorite([targetUserId])).unwrap();
                        } else if (currentUserFavorite.userIds && !currentUserFavorite.userIds.some(u => u.id === targetUserId)) {
                            const updatedUserIds = [...currentUserFavorite.userIds.map(u => u.id), targetUserId];
                            await dispatch(updateFavorite({
                                id: currentUserFavorite.id,
                                userIds: updatedUserIds,
                                userId: user?.id || ""
                            })).unwrap();
                        }
                    }
                } catch (error) {
                    // Handle error silently
                }
            }
        };

        syncLocalFavoritesWithBackend();
    }, [isAuthenticated, user?.id, targetUserId, currentUserFavorite]);

    // Load favorite status from localStorage or backend
    const loadFavoriteStatus = async () => {
        try {
    
            
            if (isAuthenticated && user?.id) {
                // For authenticated users, get from backend
        
                await dispatch(getFavoriteByUserId(user.id));
            }
            // For unauthenticated users, the status will be checked in the currentUserFavorite useEffect
                } catch (error) {
        }
    };

    // Toggle favorite status
    const handleToggleFavorite = async () => {
        if (favoriteLoading || !targetUserId || targetUserId === "" || targetUserId === "undefined") {
            return;
        }

        try {
            setFavoriteLoading(true);

            if (isAuthenticated && user?.id) {
                // For authenticated users, update backend
                let updatedUserIds;

                if (currentUserFavorite && currentUserFavorite.userIds) {
                    // Update existing favorite
                    if (isFavorite) {
                        // Remove from favorites
                        updatedUserIds = currentUserFavorite.userIds.map(user => user.id).filter(id => id !== targetUserId);
                    } else {
                        // Add to favorites (no duplicates)
                        const existingIds = currentUserFavorite.userIds.map(user => user.id);
                        if (!existingIds.includes(targetUserId)) {
                            updatedUserIds = [...existingIds, targetUserId];
                        } else {
                            updatedUserIds = existingIds;
                        }
                    }

                    await dispatch(updateFavorite({
                        id: currentUserFavorite.id,
                        userIds: updatedUserIds,
                        userId: user?.id || ""
                    })).unwrap();
                } else {
                    // Create new favorite
                    await dispatch(createFavorite([targetUserId])).unwrap();
                }

                // Update local state immediately for better UX
                setIsFavorite(!isFavorite);
            } else {
                // For unauthenticated users, update localStorage
                const favorites = await GlobalService.getStorage("favoriteUserIds");
                const favoriteIds = Array.isArray(favorites) ? favorites : [];

                let updatedFavorites;
                if (isFavorite) {
                    // Remove from favorites
                    updatedFavorites = favoriteIds.filter(id => id !== targetUserId);
                } else {
                    // Add to favorites (no duplicates)
                    if (!favoriteIds.includes(targetUserId)) {
                        updatedFavorites = [...favoriteIds, targetUserId];
                    } else {
                        updatedFavorites = favoriteIds;
                    }
                }

                await GlobalService.setStorage("favoriteUserIds", updatedFavorites);
                // Update local state immediately for better UX
                setIsFavorite(!isFavorite);
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
            // Revert the state change on error
            setIsFavorite(isFavorite);
        } finally {
            setFavoriteLoading(false);
        }
    };

    const getSizeClasses = () => {
        switch (size) {
            case "sm":
                return "w-4 h-4";
            case "lg":
                return "w-6 h-6";
            default:
                return "w-4 h-4";
        }
    };

    const getButtonClasses = () => {
        const baseClasses = "transition-colors disabled:opacity-50";
        const defaultClasses = "p-2 rounded-full bg-white shadow-lg hover:bg-gray-50";
        
        // If className is provided and contains custom styling, use it; otherwise use default
        if (className && (className.includes("p-") || className.includes("bg-") || className.includes("shadow-"))) {
            return `${baseClasses} ${className}`;
        }
        return `${baseClasses} ${defaultClasses} ${className}`;
    };

    return (
        <button
            onClick={handleToggleFavorite}
            disabled={favoriteLoading}
            className={getButtonClasses()}
            title={isFavorite ? t("favoriteButton.removeFromFavorites") : t("favoriteButton.addToFavorites")}
        >
            <div className="flex items-center gap-2">
                <Star
                    className={`${getSizeClasses()} ${isFavorite ? "text-yellow-400 fill-current" : "text-gray-400"}`}
                />
                {showText && (
                    <span className="text-sm text-white-500">
                        {isFavorite ? t("favoriteButton.removeFavorite") : t("favoriteButton.addToFavorite")}
                    </span>
                )}
            </div>
        </button>
    );
};

export default FavoriteButton;
